<script setup lang="ts">
defineProps<{
  showRescheduleMetaPanel: boolean
  unassignEnabled: boolean
  assignEnabled: boolean
  isRescheduleMode: boolean
  embedded: boolean
}>()

const emit = defineEmits<{
  unassign: []
  assign: []
}>()
</script>

<template>
  <div
    class="calendar-foot"
    :class="{ 'schedule-ctx--embedded': embedded, 'schedule-ctx--reschedule': isRescheduleMode }"
  >
    <div v-if="showRescheduleMetaPanel" class="calendar-reschedule-meta">
      <div
        class="calendar-reschedule-meta-col calendar-reschedule-meta-col--original-placeholder"
        aria-hidden="true"
      />
      <div class="calendar-reschedule-meta-divider-placeholder" aria-hidden="true" />
      <div
        class="calendar-reschedule-meta-col calendar-reschedule-meta-col--preferred-placeholder"
        aria-hidden="true"
      />
    </div>
    <div class="calendar-actions">
      <button
        type="button"
        class="btn-unassign"
        :disabled="!unassignEnabled"
        @click="emit('unassign')"
      >
        Unassign
      </button>
      <button
        type="button"
        class="btn-assign"
        :class="{ 'btn-assign--inactive': !assignEnabled }"
        :disabled="!assignEnabled"
        :aria-disabled="!assignEnabled || undefined"
        @click.stop="emit('assign')"
      >
        <span class="btn-assign-label">Assign</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.calendar-foot.schedule-ctx--embedded {
  position: absolute;
  left: 17px;
  right: 15px;
  bottom: 0;
  z-index: 20;
  margin: 0;
  padding: 12px 0 24px;
  border-top: 1px solid #f3f4f6;
  background: #f5f6fb;
  box-sizing: border-box;
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  pointer-events: auto;
}

.calendar-foot.schedule-ctx--embedded .calendar-actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 16px;
  margin-left: auto;
}

.calendar-foot {
  flex-shrink: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
}

.calendar-reschedule-meta {
  flex: 1 1 auto;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  min-width: 0;
  margin-right: auto;
}

.calendar-reschedule-meta-col--original-placeholder {
  flex: 1 1 0;
  min-width: 0;
  padding-left: 0;
  visibility: hidden;
  pointer-events: none;
}

.calendar-reschedule-meta-divider-placeholder {
  flex-shrink: 0;
  width: 1px;
  visibility: hidden;
  pointer-events: none;
}

.calendar-reschedule-meta-col--preferred-placeholder {
  flex: 1 1 0;
  min-width: 0;
  padding-right: 0;
  visibility: hidden;
  pointer-events: none;
}

.calendar-actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 16px;
  margin-left: auto;
}

.btn-unassign {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  padding: 6px 8px;
  box-sizing: border-box;
  border: 1px solid #cccccc;
  border-radius: 16px;
  background: #ffffff;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-weight: 600;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #cccccc;
  text-align: right;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  white-space: nowrap;
}

.btn-unassign:not(:disabled) {
  color: #000000;
  border-color: #000000;
}

.btn-unassign:hover:not(:disabled) {
  color: #000000;
  border-color: #000000;
}

.btn-unassign:disabled {
  color: #cccccc;
  border: 1px solid #cccccc;
  background: #ffffff;
  cursor: not-allowed;
}

.btn-assign {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  padding: 4px 17px;
  box-sizing: border-box;
  border: none;
  border-radius: 16px;
  background: #000000;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-weight: 600;
  font-size: 14px;
  font-style: normal;
  line-height: 24px;
  letter-spacing: 0;
  color: #ffffff;
  text-align: left;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
}

.btn-assign:disabled,
.btn-assign.btn-assign--inactive {
  background: #cccccc;
  border-radius: 16px;
  color: #ffffff;
  cursor: not-allowed;
}

.schedule-ctx--reschedule .btn-assign {
  padding: 4px 17px 4px 16px;
  justify-content: flex-start;
  align-items: center;
  line-height: 20px;
}

.schedule-ctx--reschedule .btn-assign-label {
  display: block;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-weight: 700;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
}

.schedule-ctx--reschedule .btn-assign:disabled,
.schedule-ctx--reschedule .btn-assign.btn-assign--inactive {
  background: #cccccc;
  color: #ffffff;
}
</style>
